(() => {
  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const money = (cents) => {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, window.theme?.moneyFormat);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  };

  const sortBy = (items, order, keyFn) =>
    [...items].sort((a, b) => {
      const ia = order.indexOf(keyFn(a));
      const ib = order.indexOf(keyFn(b));
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

  const parseSizeLabel = (size) => {
    const value = String(size || '');
    const match = value.match(/^(\d+)\s*lb/i);
    if (match) return { headline: match[1], kicker: 'lb bags' };
    if (/^full\s*bag$/i.test(value)) return { headline: 'Full', kicker: 'Bag' };
    return { headline: value, kicker: '' };
  };

  const cheapestFrom = (theme) => {
    let min = null;
    (theme?.variants || []).forEach((variant) => {
      const prices = (variant.allocations || []).map((alloc) => alloc.price);
      if (!prices.length) prices.push(variant.price);
      prices.forEach((price) => {
        if (min == null || price < min) min = price;
      });
    });
    return min;
  };

  const findVariant = (theme, size) =>
    (theme?.variants || []).find((variant) => variant.size === size) || null;

  const findAllocation = (variant, planId) =>
    (variant?.allocations || []).find((alloc) => String(alloc.selling_plan) === String(planId)) || null;

  const init = (root) => {
    if (!root || root.dataset.clubReady === 'true') return;
    const jsonEl = root.querySelector('[data-club-json]');
    if (!jsonEl) return;

    let data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }

    const ui = data.ui || {};
    const themes = (data.themes || []).filter((theme) => theme && theme.id);
    const sizeMeta =
      data.sizes && data.sizes.length
        ? data.sizes
        : Object.entries(data.sizeCopy || {}).map(([value, copy]) => ({
            value,
            copy,
            popular: Boolean(data.popularSize) && data.popularSize !== 'none' && value === data.popularSize,
          }));
    const sizeOrder = sizeMeta.map((item) => item.value).filter(Boolean);
    const maxPacks = Number(ui.maxPacks) > 0 ? Number(ui.maxPacks) : 99;
    root.dataset.clubReady = 'true';

    const catalogSizes = () => {
      const seen = new Set();
      const values = [];
      const add = (size) => {
        if (size && !/sample/i.test(size) && !seen.has(size)) {
          seen.add(size);
          values.push(size);
        }
      };
      sizeOrder.forEach(add);
      themes.forEach((theme) => (theme.variants || []).forEach((variant) => add(variant.size)));
      return sortBy(
        values.map((size) => ({ size })),
        sizeOrder.length ? sizeOrder : values,
        (item) => item.size
      );
    };

    const catalogPlans = () => {
      const seen = new Map();
      themes.forEach((theme) => {
        (theme.sellingPlans || []).forEach((plan) => {
          if (plan?.name && !seen.has(plan.name)) seen.set(plan.name, plan);
        });
      });
      return sortBy(
        [...seen.values()],
        [ui.defaultPlanName, 'Every month', 'Every 2 months', 'Every 3 months'].filter(Boolean),
        (plan) => plan.name
      );
    };

    const sizes = catalogSizes();
    const defaultPlanName = ui.defaultPlanName || 'Every month';

    const state = {
      themeId: null,
      size: null,
      planName: null,
      qty: 1,
    };

    const themeGrid = root.querySelector('[data-club-themes]');
    const sizeGrid = root.querySelector('[data-club-sizes]');
    const planGrid = root.querySelector('[data-club-plans]');
    const summaryEl = root.querySelector('[data-club-summary]');
    const priceEl = root.querySelector('[data-club-price]');
    const qtyEl = root.querySelector('[data-club-qty]');
    const errorEl = root.querySelector('[data-club-error]');
    const joinBtn = root.querySelector('[data-club-join]');
    const joinLabel = root.querySelector('[data-club-join-label]');
    const idleLabel = ui.joinLabel || joinBtn?.dataset.labelIdle || 'Join the Club';
    const blockedLabel = ui.joinLabelBlocked || joinBtn?.dataset.labelBlocked || 'Join the Club';

    const selectedTheme = () => themes.find((theme) => String(theme.id) === String(state.themeId)) || null;
    const sizeInfo = (value) => sizeMeta.find((item) => item.value === value) || {};
    const planOnTheme = (theme, name = state.planName) =>
      (theme?.sellingPlans || []).find((plan) => plan.name === name) || null;

    const currentLine = () => {
      const theme = selectedTheme();
      const variant = findVariant(theme, state.size);
      const plan = planOnTheme(theme);
      const allocation = findAllocation(variant, plan?.id);
      const unit = allocation?.price ?? variant?.price ?? null;
      return { theme, variant, plan, allocation, unit, total: unit == null ? null : unit * state.qty };
    };

    const setError = (message) => {
      if (!errorEl) return;
      errorEl.hidden = !message;
      errorEl.textContent = message || '';
    };

    const renderThemes = () => {
      if (!themeGrid) return;
      themeGrid.innerHTML = themes
        .map((theme) => {
          const selected = String(theme.id) === String(state.themeId);
          const fromPrice = cheapestFrom(theme);
          const perks = (theme.perks || [])
            .filter(Boolean)
            .map((perk) => `<li><span aria-hidden="true">✦</span><span>${escapeHtml(perk)}</span></li>`)
            .join('');
          const selectLabel = selected ? 'Selected' : `Select ${theme.title}`;
          return `
            <article class="showroom-coffee-club-adventure__card${selected ? ' is-selected' : ''}" style="--club-accent:${escapeHtml(theme.accent || '#00a7dc')};--club-shadow:${escapeHtml(theme.shadow || '#d9b46a')};">
              <span class="showroom-coffee-club-adventure__card-accent"></span>
              <div class="showroom-coffee-club-adventure__card-body">
                <span class="showroom-coffee-club-adventure__icon-wrap">
                  <img src="${escapeHtml(theme.icon)}" width="30" height="30" alt="">
                </span>
                <h3>${escapeHtml(theme.title)}</h3>
                ${theme.eyebrow ? `<p class="showroom-coffee-club-adventure__eyebrow">${escapeHtml(theme.eyebrow)}</p>` : ''}
                ${theme.description ? `<p class="showroom-coffee-club-adventure__desc">${escapeHtml(theme.description)}</p>` : ''}
                ${perks ? `<ul class="showroom-coffee-club-adventure__perks">${perks}</ul>` : ''}
                <p class="showroom-coffee-club-adventure__from">
                  <span>from</span>
                  <strong>${fromPrice == null ? '—' : escapeHtml(money(fromPrice))}</strong>
                  <span>/ shipment</span>
                </p>
                <button
                  type="button"
                  class="push-btn z-0 showroom-coffee-club-adventure__select"
                  data-club-select-theme="${escapeHtml(theme.id)}"
                  aria-pressed="${selected}"
                >
                  <span class="push-btn__surface">${escapeHtml(selectLabel)}</span>
                </button>
              </div>
            </article>
          `;
        })
        .join('');
    };

    const chosenMark = (selected) =>
      selected
        ? `<span class="showroom-coffee-club-adventure__chosen" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="#d95033"/>
              <path d="M5.75 10.25 8.5 13l5.75-6.5" stroke="#f0e5d5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>`
        : '';

    const renderSizes = () => {
      if (!sizeGrid) return;
      const theme = selectedTheme();
      const list = catalogSizes();
      if (!list.length) {
        sizeGrid.innerHTML = `<p class="showroom-coffee-club-adventure__hint">${escapeHtml(
          ui.sizeHint || 'Bag sizes will show once a Coffee Club product is assigned.'
        )}</p>`;
        return;
      }
      sizeGrid.innerHTML = list
        .map((item) => {
          const variant = theme ? findVariant(theme, item.size) : { size: item.size, available: true };
          const available = Boolean(theme && variant && variant.available);
          const selected = item.size === state.size;
          const meta = sizeInfo(item.size);
          const parsed = parseSizeLabel(item.size);
          const headline = meta.headline || parsed.headline;
          const kicker = meta.kicker || parsed.kicker;
          const copy = meta.copy || '';
          return `
            <button
              type="button"
              class="showroom-coffee-club-adventure__size${selected ? ' is-selected' : ''}"
              data-club-select-size="${escapeHtml(item.size)}"
              aria-pressed="${selected}"
              ${available ? '' : 'disabled'}
            >
              ${chosenMark(selected)}
              ${meta.popular ? `<span class="showroom-coffee-club-adventure__popular">${escapeHtml(ui.popularLabel || 'Popular')}</span>` : ''}
              <span class="showroom-coffee-club-adventure__size-num">${escapeHtml(headline)}</span>
              ${kicker ? `<span class="showroom-coffee-club-adventure__size-kicker">${escapeHtml(kicker)}</span>` : ''}
              ${copy ? `<span class="showroom-coffee-club-adventure__size-copy">${escapeHtml(copy)}</span>` : ''}
            </button>
          `;
        })
        .join('');
    };

    const renderPlans = () => {
      if (!planGrid) return;
      const theme = selectedTheme();
      const list = catalogPlans();
      if (!list.length) {
        planGrid.innerHTML = `<p class="showroom-coffee-club-adventure__hint">${escapeHtml(
          ui.noPlansHint || 'Subscription plans are not on these products yet.'
        )}</p>`;
        return;
      }
      planGrid.innerHTML = list
        .map((plan) => {
          const selected = plan.name === state.planName;
          const available = Boolean(theme && planOnTheme(theme, plan.name));
          return `
            <button
              type="button"
              class="showroom-coffee-club-adventure__plan${selected ? ' is-selected' : ''}"
              data-club-select-plan="${escapeHtml(plan.name)}"
              aria-pressed="${selected}"
              ${available ? '' : 'disabled'}
            >
              ${chosenMark(selected)}
              ${escapeHtml(plan.name)}
            </button>
          `;
        })
        .join('');
    };

    const cadencePhrase = (planName) => {
      const name = String(planName || '').trim();
      if (!name) return '';
      const lower = name.replace(/^every\s+/i, 'every ');
      return `shipped ${lower.charAt(0).toLowerCase()}${lower.slice(1)}`;
    };

    const lockJoinWidth = () => {
      if (!joinBtn || !joinLabel) return;
      const previous = joinLabel.textContent;
      joinLabel.textContent = idleLabel;
      const width = Math.ceil(joinBtn.getBoundingClientRect().width);
      joinLabel.textContent = previous;
      if (width > 0) joinBtn.style.minWidth = `${width}px`;
    };

    const renderBar = () => {
      const { theme, variant, plan, unit, total } = currentLine();
      const ready = Boolean(theme && variant && plan && state.qty > 0);
      if (qtyEl) qtyEl.textContent = String(state.qty);
      if (summaryEl) {
        if (ready) {
          const bagWord = state.qty === 1 ? 'bag' : 'bags';
          summaryEl.textContent = `You’ll get ${state.qty} ${bagWord} of ${theme.title} (${variant.size}), ${cadencePhrase(
            plan.name
          )}.`;
        } else {
          summaryEl.textContent = ui.emptySummary || 'Select a tier to start building your membership';
        }
      }
      if (priceEl) {
        priceEl.hidden = !ready;
        if (ready) {
          const each =
            unit !== total
              ? `<span class="showroom-coffee-club-adventure__unit">(${escapeHtml(money(unit))} ${escapeHtml(
                  ui.eachLabel || 'each'
                )})</span>`
              : '';
          priceEl.innerHTML = `<strong>${escapeHtml(money(total))}</strong><span class="showroom-coffee-club-adventure__cadence">/ shipment</span>${each}`;
        }
      }
      if (joinBtn && joinLabel) {
        joinBtn.disabled = !ready;
        joinBtn.classList.toggle('is-ready', ready);
        joinLabel.textContent = ready ? idleLabel : blockedLabel;
      }
    };

    const render = () => {
      renderThemes();
      renderSizes();
      renderPlans();
      renderBar();
    };

    const selectTheme = (id) => {
      const theme = themes.find((item) => String(item.id) === String(id));
      if (!theme) return;
      state.themeId = theme.id;
      const availableSizes = (theme.variants || []).filter((variant) => variant.available);
      if (!availableSizes.some((variant) => variant.size === state.size)) {
        state.size =
          availableSizes.find((variant) => variant.size === (sizes[0]?.size || '2 lb'))?.size ||
          availableSizes[0]?.size ||
          theme.variants?.[0]?.size ||
          null;
      }
      if (!planOnTheme(theme)) {
        const themePlans = theme.sellingPlans || [];
        state.planName =
          (themePlans.find((plan) => plan.name === defaultPlanName) || themePlans[0])?.name || null;
      }
      setError('');
      render();
    };

    root.addEventListener('click', (event) => {
      const themeBtn = event.target.closest('[data-club-select-theme]');
      if (themeBtn) {
        selectTheme(themeBtn.getAttribute('data-club-select-theme'));
        return;
      }
      const sizeBtn = event.target.closest('[data-club-select-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        state.size = sizeBtn.getAttribute('data-club-select-size');
        setError('');
        render();
        return;
      }
      const planBtn = event.target.closest('[data-club-select-plan]');
      if (planBtn && !planBtn.disabled) {
        state.planName = planBtn.getAttribute('data-club-select-plan');
        setError('');
        render();
        return;
      }
      const qtyBtn = event.target.closest('[data-club-qty-change]');
      if (qtyBtn) {
        const next = state.qty + Number(qtyBtn.getAttribute('data-club-qty-change'));
        state.qty = Math.max(1, Math.min(maxPacks, next));
        render();
      }
    });

    joinBtn?.addEventListener('click', async () => {
      const { theme, variant, plan, allocation } = currentLine();
      if (!theme || !variant || !plan) return;
      if (!allocation && (theme.sellingPlans || []).length) {
        setError(ui.missingPlanError || 'This size is missing a subscription price. Try another size.');
        return;
      }
      setError('');
      lockJoinWidth();
      joinBtn.disabled = true;
      joinLabel.textContent = ui.addingLabel || 'Adding…';
      try {
        const modalCart = String(window.theme?.settings?.cartType || '').toLowerCase() === 'modal';
        const item = {
          id: variant.id,
          quantity: state.qty,
          selling_plan: Number(plan.id),
          properties: {
            Theme: theme.title,
            'Bag Size': variant.size,
          },
        };
        const payload = { items: [item] };
        if (modalCart) {
          payload.sections = 'cart-items,cart-footer,cart-item-count';
          payload.sections_url = window.location.pathname;
        }
        const addUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || ui.cartError || 'Could not add Coffee Club to cart.');
        }
        document.dispatchEvent(new CustomEvent('theme:update:cart'));
        if (modalCart) {
          document.body.dispatchEvent(
            new CustomEvent('shapes:cart:afteradditem', {
              bubbles: true,
              detail: { response: result, sourceId: root.id || 'coffee-club-adventure' },
            })
          );
        }
        joinLabel.textContent = ui.addedLabel || 'Added';
        window.setTimeout(renderBar, 1200);
      } catch (error) {
        setError(error.message || ui.cartError || 'Could not add Coffee Club to cart.');
        renderBar();
      }
    });

    render();
    lockJoinWidth();
  };

  const bindAdventureCtas = () => {
    document.querySelectorAll('[data-club-adventure-cta]').forEach((link) => {
      const sections = [...document.querySelectorAll('[data-coffee-club-adventure]')];
      const target =
        sections.find((el) => link.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) || sections[0];
      if (!target) return;
      if (!target.id) target.id = 'choose-your-adventure';
      link.setAttribute('href', `#${target.id}`);
    });
  };

  const boot = (scope) => {
    (scope || document).querySelectorAll('[data-coffee-club-adventure]').forEach(init);
    bindAdventureCtas();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
